import React from "react";
import { useChunkProgress } from "../hooks/useChunkProgress";
import { CheckCircle2, Loader, XCircle, Clock } from "lucide-react";
import Skeleton from "@/components/ui/Skeleton";

interface ChunkProgressDisplayProps {
  jobId: string;
  className?: string;
}

export const ChunkProgressDisplay: React.FC<ChunkProgressDisplayProps> = ({
  jobId,
  className = "",
}) => {
  const { chunks, isLoading } = useChunkProgress(jobId);

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
    );
  }

  if (chunks.length === 0) {
    return null;
  }

  const completed = chunks.filter((c) => c.status === "completed").length;
  const processing = chunks.filter((c) => c.status === "processing").length;
  const failed = chunks.filter((c) => c.status === "failed").length;
  const pending = chunks.filter((c) => c.status === "pending").length;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Summary */}
      <div className="flex items-center gap-3 text-xs">
        <span className="text-white/60">
          Chunks: {completed}/{chunks.length}
        </span>
        {processing > 0 && (
          <span className="flex items-center gap-1 text-blue-400">
            <Loader size={12} className="animate-spin" />
            {processing} en cours
          </span>
        )}
        {failed > 0 && (
          <span className="flex items-center gap-1 text-rose-400">
            <XCircle size={12} />
            {failed} échec(s)
          </span>
        )}
      </div>

      {/* Chunk Grid */}
      <div className="grid grid-cols-8 gap-1">
        {chunks.map((chunk) => (
          <div
            key={chunk.id}
            className={`h-6 rounded flex items-center justify-center text-xs font-medium transition-all ${
              chunk.status === "completed"
                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                : chunk.status === "processing"
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30 animate-pulse"
                : chunk.status === "failed"
                ? "bg-red-500/20 text-rose-400 border border-red-500/30"
                : "bg-white/10 text-white/40 border border-white/10"
            }`}
            title={`Chunk ${chunk.chunkIndex + 1}: ${chunk.status} (${
              chunk.ordersCount
            } PDFs)${
              chunk.errorMessage ? `\nErreur: ${chunk.errorMessage}` : ""
            }`}
          >
            {chunk.status === "completed" && <CheckCircle2 size={14} />}
            {chunk.status === "processing" && (
              <Loader size={14} className="animate-spin" />
            )}
            {chunk.status === "failed" && <XCircle size={14} />}
            {chunk.status === "pending" && <Clock size={14} />}
          </div>
        ))}
      </div>
    </div>
  );
};
